/* ══════════════════════════════════════════════════════════════════════════
   Slot art — flat illustrated placeholders.
   No photography has landed yet, so every photo well draws its product as
   flat Modernist geometry (token colours, hard offset shadows) instead of a
   bare gray box. The shot brief stays visible as a chip, so the shoot list
   is still legible on the page — these REMAIN placeholders, not final art.
   When real photos land: drop an <img> into the well and delete this file.
   ══════════════════════════════════════════════════════════════════════════ */
(function (window, document) {
  'use strict';

  var INK = 'var(--color-text)';
  var COL = {
    red: 'var(--color-accent)',
    ink: 'var(--color-neutral-800)',
    fog: 'var(--color-neutral-400)'
  };

  /* ───────────────────────────  primitives  ────────────────────────────── */
  function grp(inner, x, y, sx, sy, rot) {
    return '<g transform="translate(' + x + ' ' + y + ')' +
      (rot ? ' rotate(' + rot + ')' : '') +
      ' scale(' + sx + ' ' + (sy || sx) + ')">' + inner + '</g>';
  }

  /* Each shape is drawn around its own origin: hard ink shadow first
     (offset +10,+12 like the keycap buttons), then the body. */
  function cube(color) {
    return '<rect x="-56" y="-54" width="132" height="132" fill="' + INK + '"/>' +
      '<rect x="-66" y="-66" width="132" height="132" fill="' + color + '" stroke="' + INK + '" stroke-width="6"/>';
  }
  function ball(color) {
    return '<circle cx="10" cy="12" r="66" fill="' + INK + '"/>' +
      '<circle cx="0" cy="0" r="66" fill="' + color + '" stroke="' + INK + '" stroke-width="6"/>';
  }
  function doughnut(color) {
    return '<circle cx="10" cy="12" r="47" fill="none" stroke="' + INK + '" stroke-width="38"/>' +
      '<circle cx="0" cy="0" r="47" fill="none" stroke="' + color + '" stroke-width="38"/>' +
      '<circle cx="0" cy="0" r="66" fill="none" stroke="' + INK + '" stroke-width="6"/>' +
      '<circle cx="0" cy="0" r="28" fill="none" stroke="' + INK + '" stroke-width="6"/>';
  }

  var DRAW = { cube: cube, ball: ball, doughnut: doughnut };

  function shapeAt(kind, color, x, y, s, sy, rot) {
    return grp(DRAW[kind](color), x, y, s, sy || s, rot || 0);
  }

  /* the surface the product "sits" on */
  function baseline(y) {
    return '<line x1="70" y1="' + y + '" x2="330" y2="' + y +
      '" stroke="var(--color-neutral-400)" stroke-width="2"/>';
  }

  function svg(inner) {
    return '<svg viewBox="0 0 400 400" preserveAspectRatio="xMidYMid meet" aria-hidden="true">' + inner + '</svg>';
  }

  /* ───────────────────────────  compositions  ──────────────────────────── */
  function packshot(kind, color) {
    return svg(baseline(298) + shapeAt(kind, color, 200, 212, 1.25));
  }

  function trio() {
    return svg(
      baseline(316) +
      shapeAt('doughnut', COL.fog, 128, 190, .9) +
      shapeAt('cube', COL.red, 268, 182, .95) +
      shapeAt('ball', COL.ink, 200, 264, .78)
    );
  }

  function strip(kind, color, rot) {
    return svg(baseline(306) + shapeAt(kind, color, 200, 214, 1.15, 1.15, rot));
  }

  /* PDP gallery frames — each one says something different, like the shots will */
  function frame(kind, color, i, sizeLabel) {
    if (i === 1) {
      /* in hand, mid-squish: the shape pressed flat, motion ticks above */
      return svg(
        baseline(300) +
        '<g stroke="' + INK + '" stroke-width="5" stroke-linecap="round">' +
          '<path d="M158 128 l-14 -22"/><path d="M200 118 l0 -26"/><path d="M242 128 l14 -22"/>' +
        '</g>' +
        shapeAt(kind, color, 200, 242, 1.5, .62)
      );
    }
    if (i === 2) {
      /* texture close-up: the shape repeated as a tight pattern, one lit */
      var cells = '';
      for (var r = 0; r < 3; r++) {
        for (var c = 0; c < 3; c++) {
          var lit = (r === 1 && c === 1);
          cells += grp(
            lit ? DRAW[kind](color)
                : DRAW[kind]('var(--color-neutral-200)'),
            80 + c * 120, 84 + r * 120, .42
          );
        }
      }
      return svg(cells);
    }
    if (i === 3) {
      /* scale: the shape over a ruled line, printing its real dimensions */
      return svg(
        shapeAt(kind, color, 200, 168, 1.05) +
        '<g stroke="' + INK + '" stroke-width="4">' +
          '<line x1="60" y1="316" x2="340" y2="316"/>' +
          '<line x1="60" y1="304" x2="60" y2="328"/><line x1="340" y1="304" x2="340" y2="328"/>' +
          '<line x1="200" y1="310" x2="200" y2="322"/>' +
        '</g>' +
        '<text x="200" y="352" text-anchor="middle" font-family="Poppins, sans-serif" font-size="15" font-weight="500" letter-spacing="1" fill="var(--color-neutral-700)">' + (sizeLabel || '') + '</text>'
      );
    }
    if (i === 4) {
      /* the box: carton + the numbered collector card poking out */
      return svg(
        baseline(322) +
        '<rect x="100" y="140" width="200" height="180" fill="var(--color-bg)" stroke="' + INK + '" stroke-width="6"/>' +
        '<path d="M100 140 L130 96 L270 96 L300 140" fill="var(--color-bg)" stroke="' + INK + '" stroke-width="6" stroke-linejoin="round"/>' +
        shapeAt(kind, color, 200, 236, .58) +
        grp('<rect x="-36" y="-24" width="72" height="48" fill="var(--color-bg)" stroke="' + INK + '" stroke-width="4"/>' +
            '<line x1="-22" y1="-6" x2="22" y2="-6" stroke="' + INK + '" stroke-width="4"/>' +
            '<line x1="-22" y1="8" x2="10" y2="8" stroke="var(--color-neutral-500)" stroke-width="4"/>',
            296, 132, 1, 1, 8)
      );
    }
    /* 0 — packshot */
    return packshot(kind, color);
  }

  /* ───────────────────────  slot id → composition  ─────────────────────── */
  var BAND = [
    ['cube', 'red', -6], ['ball', 'ink', 5], ['doughnut', 'fog', -5],
    ['cube', 'fog', 6], ['ball', 'red', -6], null /* trio */
  ];

  function artFor(id) {
    var m;
    if ((m = id.match(/^band-(\d)$/))) {
      var b = BAND[+m[1] - 1];
      return b ? strip(b[0], COL[b[1]], b[2]) : trio();
    }
    if (id === 'bundle-set' || id === 'coll-set') return trio();
    /* the illustration layer no longer reads the catalogue: colours are the
       fixed brand trio per drawn shape (the real SKUs use photography now) */
    var KIND_COL = { cube: COL.red, ball: COL.ink, doughnut: COL.fog };
    if ((m = id.match(/^(?:prod|card|qv)-(cube|ball|doughnut)$/))) {
      return packshot(m[1], KIND_COL[m[1]]);
    }
    if ((m = id.match(/^pdp-(cube|ball|doughnut)-(\d)$/))) {
      return frame(m[1], KIND_COL[m[1]], +m[2], '');
    }
    if ((m = id.match(/^(?:review|pdp-rev)-(\d)$/))) {
      /* review photos grayscale via the parent's filter; cycle the shapes */
      var kinds = ['cube', 'ball', 'doughnut'];
      var k = kinds[(+m[1] - 1) % 3];
      return packshot(k, KIND_COL[k]);
    }
    return null;
  }

  /* ─────────────────────────────  upgrade  ─────────────────────────────── */
  function esc(s) {
    return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  function upgradeAll() {
    document.querySelectorAll('.slot[data-slot]:not(.slot--art)').forEach(function (slot) {
      var art = artFor(slot.getAttribute('data-slot'));
      if (!art) return;
      var briefEl = slot.querySelector('span');
      var brief = briefEl ? briefEl.textContent : '';
      slot.classList.add('slot--art');
      slot.innerHTML = art +
        '<span class="slot-cross" aria-hidden="true"></span>' +
        '<span class="slot-brief"><b>Photo</b>' + esc(brief) + '</span>';
    });
  }

  window.SQArt = { upgradeAll: upgradeAll };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', upgradeAll);
  else upgradeAll();

})(window, document);
